import { get } from 'lodash';
import * as Timestamps from '@/types/timestamp';
import { loadStatus } from '@/types';
import moment from 'moment';

export const tacticalInfo = load => get(load, 'tacticalInfo', {});
export const listingDetails = load => get(load, 'tacticalInfo.loadDetails', {});

export const listingCompany = load => get(load, 'tacticalInfo.loadDetails.listingCompany', {});
export const carrierCompany = load => get(load, 'tacticalInfo.truckingCompany', {});
export const assignedVendor = load => get(load, 'tacticalInfo.assignedVendor');
// basically, for the purposes of presentation and filtering, we need a company.
export const haulingCompany = load => assignedVendor(load) || carrierCompany(load);

export const dropoffCompany = load => get(load, 'tacticalInfo.loadDetails.dropoffCompany', {}) || {};
export const dropoffLocation = load => get(load, 'tacticalInfo.loadDetails.dropoffLocation', {}) || {};
export const dropoffLocationChute = load => get(load, 'tacticalInfo.loadDetails.dropoffLocation.chute', {}) || {};
export const dropoffLocationGooglePlace = load => get(load, 'tacticalInfo.loadDetails.dropoffLocation.googlePlace', {}) || {};
export const dropoffLocationTimezone = load => get(load, 'tacticalInfo.loadDetails.dropoffLocation.timezone');
// this is a local date time
export const deliveryEstimated = load => get(load, 'tacticalInfo.estimatedDelivery');
export const delivered = load => get(load, 'delivery.when');
export const deliveredLocalTime = (load) => {
  const ts = delivered(load);
  const timezone = dropoffLocationTimezone(load);
  return Timestamps.localizedTimestamp(ts, timezone);
};

export const pickupCompany = load => get(load, 'tacticalInfo.loadDetails.pickupCompany', {}) || {};
export const pickupLocation = load => get(load, 'tacticalInfo.loadDetails.pickupLocation', {}) || {};
export const pickupLocationChute = load => get(load, 'tacticalInfo.loadDetails.pickupLocation.chute', {}) || {};
export const pickupLocationGooglePlace = load => get(load, 'tacticalInfo.loadDetails.pickupLocation.googlePlace', {}) || {};
export const pickupLocationTimezone = load => get(load, 'tacticalInfo.loadDetails.pickupLocation.timezone');
// this is a local date time
export const pickupScheduled = load => get(load, 'tacticalInfo.scheduledPickupTime');
// this is a local date time
export const pickupEstimated = load => get(load, 'tacticalInfo.estimatedPickup');
// this is a zoned date time
export const pickedUp = load => get(load, 'pickup.when');
export const pickedUpLocalTime = (load) => {
  const ts = pickedUp(load);
  const timezone = pickupLocationTimezone(load);
  return Timestamps.localizedTimestamp(ts, timezone);
};

export const localizeTimestampToPickup = (load, date) => {
  const tz = pickupLocationTimezone(load);
  const value = Timestamps.localizedTimestamp(date, tz);
  return value;
};
export const localizeTimestampToDelivery = (load, date) => {
  const tz = dropoffLocationTimezone(load);
  return Timestamps.localizedTimestamp(date, tz);
};

export const loadedMiles = load => get(load, 'tacticalInfo.loadDetails.loadedMiles', 0);
export const assignedTruckingCompanyDriverUserId = load => get(load, 'tacticalInfo.assignedTruckingCompanyDriverUserId', null);
export const assignedTruckingCompanyDriverName = load => get(load, 'tacticalInfo.assignedTruckingCompanyDriverName', null);

export const requiresWashout = load => get(load, 'tacticalInfo.loadDetails.requirements.requireWashout', false);
export const requireWeatherboards = load => get(load, 'tacticalInfo.loadDetails.requirements.requireWeatherboards', false);
export const requireWoodChips = load => get(load, 'tacticalInfo.loadDetails.requirements.requireWoodChips', false);
export const requireDeliveryDocument = load => get(load, 'tacticalInfo.loadDetails.requirements.requireDeliveryDocument', null);
export const dispatchInstructions = load => get(load, 'tacticalInfo.loadDetails.requirements.dispatchInstructions', false);
export const driverInstructions = load => get(load, 'tacticalInfo.loadDetails.requirements.driverInstructions', false);

export const customerName = load => get(load, 'tacticalInfo.loadDetails.customerCompany.name', null);

export const customerCompany = load => get(load, 'tacticalInfo.loadDetails.customerCompany', null);
export const customerSettlement = load => get(load, 'terms.customerSettlement', null);
export const freightSettlement = load => get(load, 'terms.freightSettlement', null);

export const loadNickname = load => get(load, 'tacticalInfo.loadDetails.nickname', null);

export const notificationPickupTime = (load) => {
  let formattedTime;
  let providedBy;
  let dateObject;

  if (pickedUpLocalTime(load)) {
    dateObject = pickedUp(load);
    formattedTime = Timestamps.formattedDate(pickedUpLocalTime(load));
    providedBy = 'departure';
  } else if (pickupEstimated(load)) {
    dateObject = pickupEstimated(load);
    formattedTime = Timestamps.formattedDate(pickupEstimated(load));
    providedBy = 'carrier estimate';
  } else {
    dateObject = pickupScheduled(load);
    formattedTime = Timestamps.formattedDate(pickupScheduled(load));
    providedBy = 'scheduled';
  }

  return {
    value: formattedTime,
    dateObject,
    providedBy,
  };
};

export const notificationDeliveryTime = (load) => {
  let formattedTime;
  let providedBy;
  let dateObject;

  if (deliveredLocalTime(load)) {
    dateObject = delivered(load);
    formattedTime = Timestamps.formattedDate(deliveredLocalTime(load));
    providedBy = 'delivered';
  } else if (deliveryEstimated(load)) {
    dateObject = deliveryEstimated(load);
    formattedTime = Timestamps.formattedDate(deliveryEstimated(load));
    providedBy = 'carrier estimate';
  } else {
    const totalHours = loadedMiles(load) / 55; // 55mph
    if (load.loadStatus === loadStatus.IN_TRANSIT) {
      dateObject = pickedUp(load);
    } else {
      dateObject = pickupEstimated(load) ? pickupEstimated(load) : pickupScheduled(load);
    }

    const momentDate = moment(dateObject, moment.ISO_8601);
    momentDate.add(totalHours, 'hours');
    formattedTime = Timestamps.formattedDate(localizeTimestampToDelivery(load,
      momentDate.toISOString()));
    providedBy = 'Google estimate';
  }

  return {
    value: formattedTime,
    dateObject,
    providedBy,
  };
};

export const isDriver = (userProfile, load) => assignedTruckingCompanyDriverUserId(load) &&
      assignedTruckingCompanyDriverUserId(load) === userProfile.id;
export const billOfLadingNumber = load => get(load, 'delivery.billOfLadingNumber');

export const hoursUntilScheduledPickup = (load) => {
  const now = moment();
  const pickup = load.pickupTime;
  const momentDate = moment(pickup, moment.ISO_8601);

  const diff = momentDate.diff(now, 'hour');
  return diff;
};

export default {
  assignedTruckingCompanyDriverUserId,
  assignedTruckingCompanyDriverName,
  billOfLadingNumber,
  loadedMiles,
  listingDetails,
  tacticalInfo,
  pickupEstimated,
  pickupScheduled,
  deliveryEstimated,
  listingCompany,
  pickupCompany,
  pickupLocation,
  pickupLocationChute,
  pickupLocationGooglePlace,
  assignedVendor,
  carrierCompany,
  haulingCompany,
  dropoffCompany,
  dropoffLocation,
  dropoffLocationChute,
  dropoffLocationGooglePlace,
  notificationPickupTime,
  notificationDeliveryTime,
  requiresWashout,
  requireWeatherboards,
  requireWoodChips,
  requireDeliveryDocument,
  customerName,
  customerCompany,
  customerSettlement,
  freightSettlement,
  hoursUntilScheduledPickup,
  loadNickname,
};



// WEBPACK FOOTER //
// ./src/types/models/load.js